import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { type ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { CampusScope } from '@chancenwerk/db/Scopes.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { filter, switchMap, takeUntil } from 'rxjs';

@customElement('campus-dialog-edit-scope')
export class CampusDialogEditScope extends ChromiumDialog<void> {
  // States
  @state() public scope = new RXController<CampusScope>(this, new CampusScope({}));
  @state() private readonly _scopeDirty = new RXController(
    this,
    this.scope.observable.pipe(switchMap(scope => scope.dirty)),
  );
  @state() private readonly _exercisesInScope = new RXController(
    this,
    this.scope.observable.pipe(
      filter(scope => !!scope._id),
      switchMap(scope => CampusScope.countExercisesForScope(scope._id)()),
    ),
  );

  @state() private readonly _lessonsInScope = new RXController(
    this,
    this.scope.observable.pipe(
      filter(scope => !!scope._id),
      switchMap(scope => CampusScope.countLessonsForScope(scope._id)()),
    ),
  );

  @state() private readonly _topicsInScope = new RXController(
    this,
    this.scope.observable.pipe(
      filter(scope => !!scope._id),
      switchMap(scope => CampusScope.countTopicsForScope(scope._id)()),
    ),
  );

  @state() private readonly _eventsInScope = new RXController(
    this,
    this.scope.observable.pipe(
      filter(scope => !!scope._id),
      switchMap(scope => CampusScope.countEventsForScope(scope._id)()),
    ),
  );

  @state() private readonly _errorMsg = new RXController<string>(this, '');

  private readonly _form = new FormController(this, this.scope.observable, {
    success: async campusScope => this._handleFormSuccess(campusScope),
    error: err => this._handleFormError(err),
  });

  static override get element_name() {
    return 'campus-dialog-edit-scope';
  }

  override connectedCallback() {
    super.connectedCallback();
    this.scope.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => (this.heading = !e._id ? 'Scope erstellen' : 'Scope bearbeiten'));
  }

  private async _handleFormSuccess(campusScope: CampusScope) {
    if (!campusScope._id) await campusScope.create().catch(e => this._errorMsg.next(e.detail));
    else await campusScope.save().catch(e => this._errorMsg.next(e.detail));
    await this.close();
  }
  private _handleFormError(err: ValidationError<any>) {
    this._errorMsg.next(err.message);
  }

  public override content() {
    return html`
      <cr-flex space="md" padding="md">
        <cr-flex space="md" horizontal space-between center crossaxis-center>
          ${!this.scope.value._id
            ? nothing
            : html`
                <cr-flex space="sm">
                  <cr-text>Topics in diesem Scope: ${this._topicsInScope.value}</cr-text>
                  <cr-text>Lessons in diesem Scope: ${this._lessonsInScope.value}</cr-text>
                  <cr-text>Exercises in diesem Scope: ${this._exercisesInScope.value}</cr-text>
                  <cr-text>Events in diesem Scope: ${this._eventsInScope.value}</cr-text>
                </cr-flex>
              `}
        </cr-flex>
        ${this._errorMsg.value.length ? html`<cr-h2 ${css({ color: 'red' })}>${this._errorMsg.value}</cr-h2>` : nothing}
        <cr-input ${this._form.field('name')} placeholder="Name"></cr-input>

        <cr-flex horizontal crossaxis-center end space="md">
          ${this.scope.value._id
            ? html`
                <campus-delete-button
                  @click=${async () => {
                    if (!(await ChromiumDialog.confirm('Scope wirklich löschen?'))) return;
                    await this.scope.value.remove();
                    await this.close();
                  }}
                >
                </campus-delete-button>
              `
            : nothing}
          <cr-button @click=${() => this._form.validate()}>
            ${!this.scope.value._id ? html` Erstellen ` : html` Speichern `}
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  public override async cancel() {
    if (this._scopeDirty.value && this.scope.value._id) {
      if (!(await ChromiumDialog.confirm('Ungespeicherte Änderungen verwerfen?'))) return;
      if (this.scope.value._id) this.scope.value.revert();
    }
    await super.cancel();
  }
}
